// SchoolSearchModal.js - NEIS API 학교 검색 모달
import React, { useState, useCallback } from 'react';
import { MagnifyingGlassIcon, XMarkIcon, BuildingOfficeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// 시도교육청 목록
const REGIONS = [
  { name: '전체', code: '' },
  { name: '서울', code: 'B10' },
  { name: '부산', code: 'C10' },
  { name: '대구', code: 'D10' },
  { name: '인천', code: 'E10' },
  { name: '광주', code: 'F10' },
  { name: '대전', code: 'G10' },
  { name: '울산', code: 'H10' },
  { name: '세종', code: 'I10' },
  { name: '경기', code: 'J10' },
  { name: '강원', code: 'K10' },
  { name: '충북', code: 'M10' },
  { name: '충남', code: 'N10' },
  { name: '전북', code: 'P10' },
  { name: '전남', code: 'Q10' },
  { name: '경북', code: 'R10' },
  { name: '경남', code: 'S10' },
  { name: '제주', code: 'T10' }
];

const SchoolSearchModal = ({ isOpen, onClose, onSelectSchool, token }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [registering, setRegistering] = useState(null);

  const handleSearch = useCallback(async () => {
    if (searchQuery.trim().length < 2) {
      setError('학교명을 2자 이상 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setError('');
    setSearchResults([]);

    try {
      const regionParam = selectedRegion ? `&region=${REGIONS.find(r => r.code === selectedRegion)?.name}` : '';
      const response = await fetch(
        `${API_BASE_URL}/api/schools/search?name=${encodeURIComponent(searchQuery)}${regionParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || '학교 검색에 실패했습니다.');
      }

      if (data.data && data.data.length === 0) {
        setError('검색 결과가 없습니다. 다른 검색어를 시도해주세요.');
      } else {
        setSearchResults(data.data || []);
      }
    } catch (err) {
      setError(err.message || '학교 검색 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, selectedRegion, token]);

  const handleRegisterAndSelect = useCallback(async (school) => {
    if (school.isRegistered) {
      // 이미 등록된 학교면 바로 선택
      onSelectSchool(school);
      onClose();
      return;
    }

    setRegistering(school.neisCode);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/schools`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          neisCode: school.neisCode,
          officeCode: school.officeCode,
          name: school.name,
          engName: school.engName,
          schoolType: school.schoolType,
          address: school.address
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // 회원가입 중인 교사 - 임시 토큰 사용자 (403 Forbidden)
        if (response.status === 403 && data.requiresRegistration) {
          // 학교 정보를 임시 저장하여 회원가입 완료 시 등록
          console.log('회원가입 중 - 학교 정보 임시 저장:', school.name);
          onSelectSchool({
            ...school,
            pendingRegistration: true,
            id: null
          });
          onClose();
          return;
        }
        // 이미 등록된 경우 (409 Conflict)
        if (response.status === 409) {
          // 기존 학교 정보가 있으면 자동 선택
          if (data.data) {
            alert(`'${school.name}'은(는) 이미 등록된 학교입니다.\n해당 학교로 설정됩니다.`);
            onSelectSchool({ ...school, id: data.data.id, isRegistered: true });
            onClose();
            return;
          }
          // data.data가 없어도 메시지 표시
          throw new Error(data.message || '이미 등록된 학교입니다. 검색 결과에서 "등록됨" 표시된 학교를 선택해주세요.');
        }
        throw new Error(data.message || '학교 등록에 실패했습니다.');
      }

      // 등록 성공 - 등록된 학교 정보로 선택
      onSelectSchool({ ...school, id: data.data.id });
      onClose();
    } catch (err) {
      setError(err.message || '학교 등록 중 오류가 발생했습니다.');
    } finally {
      setRegistering(null);
    }
  }, [token, onSelectSchool, onClose]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <BuildingOfficeIcon className="w-6 h-6 text-blue-500" />
              학교 검색 (NEIS)
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Search Area */}
          <div className="p-4 border-b bg-gray-50">
            <div className="flex gap-2 mb-3">
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {REGIONS.map(region => (
                  <option key={region.code} value={region.code}>
                    {region.name}
                  </option>
                ))}
              </select>
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="학교명을 입력하세요 (예: 완도초)"
                  className="w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <MagnifyingGlassIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
              <button
                onClick={handleSearch}
                disabled={isLoading}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {isLoading ? '검색중...' : '검색'}
              </button>
            </div>
            <p className="text-sm text-gray-500">
              * 초등학교만 검색됩니다. NEIS(교육행정정보시스템) 데이터를 사용합니다.
            </p>
          </div>

          {/* Results Area */}
          <div className="overflow-y-auto max-h-96">
            {error && (
              <div className="p-4 text-center text-red-600 bg-red-50">
                {error}
              </div>
            )}

            {isLoading && (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-3"></div>
                <p className="text-gray-600">학교를 검색하고 있습니다...</p>
              </div>
            )}

            {!isLoading && searchResults.length > 0 && (
              <div className="divide-y">
                {searchResults.map((school) => (
                  <div
                    key={school.neisCode}
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900">{school.name}</h3>
                          {school.isRegistered && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                              <CheckCircleIcon className="w-3 h-3" />
                              등록됨
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {school.address || '주소 정보 없음'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {school.officeName} | 코드: {school.neisCode}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRegisterAndSelect(school)}
                        disabled={registering === school.neisCode}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          school.isRegistered
                            ? 'bg-green-500 text-white hover:bg-green-600'
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        } disabled:bg-gray-300 disabled:cursor-not-allowed`}
                      >
                        {registering === school.neisCode ? (
                          <span className="flex items-center gap-1">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            등록중
                          </span>
                        ) : school.isRegistered ? (
                          '선택'
                        ) : (
                          '등록 및 선택'
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isLoading && !error && searchResults.length === 0 && searchQuery && (
              <div className="p-8 text-center text-gray-500">
                <BuildingOfficeIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>검색어를 입력하고 검색 버튼을 눌러주세요.</p>
              </div>
            )}

            {!isLoading && !error && searchResults.length === 0 && !searchQuery && (
              <div className="p-8 text-center text-gray-500">
                <BuildingOfficeIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>학교명을 입력하여 검색해주세요.</p>
                <p className="text-sm mt-2">예: 완도초, 서울대, 한빛초</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-gray-50 text-center">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolSearchModal;
